import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock, CheckCircle, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center px-4">
          <div className="bg-card border border-border rounded-sm p-8 max-w-md text-center" data-testid="reset-password-page">
            <AlertTriangle className="h-16 w-16 text-orange-400 mx-auto" />
            <h1 className="text-2xl font-black text-foreground uppercase tracking-tight mt-4">Invalid Link</h1>
            <p className="text-muted-foreground mt-3">This password reset link is invalid or missing a token.</p>
            <Link to="/forgot-password">
              <Button className="mt-6 bg-[#78BE20] text-black hover:bg-[#65a31b]">Request New Reset Link</Button>
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="w-full max-w-md" data-testid="reset-password-page">
          {success ? (
            <div className="bg-card border border-border rounded-sm p-8 text-center">
              <CheckCircle className="h-16 w-16 text-[#78BE20] mx-auto" />
              <h1 className="text-2xl font-black text-foreground uppercase tracking-tight mt-4">Password Reset!</h1>
              <p className="text-muted-foreground mt-3">Your password has been successfully updated. You can now log in with your new password.</p>
              <Link to="/login">
                <Button className="mt-6 bg-[#78BE20] text-black hover:bg-[#65a31b]" data-testid="go-to-login-btn">Go to Login</Button>
              </Link>
            </div>
          ) : (
            <div className="bg-card border border-border rounded-sm p-8">
              <h1 className="text-2xl font-black text-foreground uppercase tracking-tight text-center">Set New Password</h1>
              <p className="text-muted-foreground text-center mt-2">Enter your new password below</p>

              {error && (
                <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded text-red-400 text-sm">{error}</div>
              )}

              <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                <div>
                  <Label htmlFor="password">New Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="password" type="password" required value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Min 6 characters"
                      className="pl-10"
                      data-testid="new-password-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="confirmPassword" type="password" required value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Repeat password"
                      className="pl-10"
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-[#78BE20] text-black hover:bg-[#65a31b]" data-testid="reset-submit-btn">
                  {loading ? 'Resetting...' : 'Reset Password'}
                </Button>
              </form>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
